import h from 'hyperscript';
import isEqual from 'lodash-es/isEqual';
import searchIcon from '@disy/cadenza-icons/search.svg';

import type { Data } from 'ui/data/data';
import { Select } from 'ui/select/select';
import { createIconButton } from 'ui/button/button';
import { setupPopupButton } from 'ui/popup/popup';

import { icon } from 'cadenza/utils/icon/icon';
import type { ConditionApiOptions } from 'cadenza/api-client/conditions/condition-api';
import { LikeConditionApi } from 'cadenza/api-client/conditions/like-condition-api';
import { ConditionElement } from 'cadenza/conditions/condition-element';
import type { CaseSensitivity } from 'cadenza/conditions/model/case-sensitivity';
import type { CombinationType } from 'cadenza/conditions/model/combination-type';
import type { FilterType } from 'cadenza/conditions/model/filter-type';
import {
  DEFAULT_CASESENSITIVITY,
  DEFAULT_COMBINATIONTYPE,
  DEFAULT_FILTERTYPE,
  LikeConditionDropdown
} from 'cadenza/conditions/like-condition/like-condition-dropdown';
import type { DefaultConditionValueItemDto, WorkbookConditionValuesDto } from 'cadenza/workbook/model/condition-dto';
import type { ConditionId } from 'cadenza/workbook/model/condition-id';

import i18n from './messages.properties';
import './like-condition.css';

const name = 'd-like-condition';

type LikeConditionOptions = Partial<{
  conditionApiSupplier: (options: ConditionApiOptions) => LikeConditionApi;
  filterType: FilterType;
  caseSensitivity: CaseSensitivity;
  combinationType: CombinationType;
  simplifiedConfiguration: boolean;
  values: WorkbookConditionValuesDto;
  isWorkbook: boolean;
}>;

export class LikeCondition extends ConditionElement {

  _conditionApiSupplier;
  // ! – initialized in `connectedCallback`
  conditionApi!: LikeConditionApi;
  // ! – initialized in `connectedCallback`
  _selectInput!: Select;
  // ! – initialized in `connectedCallback`
  #dropdownButton!: HTMLButtonElement;
  // ! – initialized before use in `onConditionChange`
  #latestSetValuesPromise!: Promise<unknown>; // for keeping consistency for other conditions, see onConditionChange
  _filterType;
  _caseSensitivity;
  _combinationType;
  _simplifiedConfiguration;

  constructor ({
    conditionApiSupplier = options => new LikeConditionApi(options),
    filterType = DEFAULT_FILTERTYPE,
    caseSensitivity = DEFAULT_CASESENSITIVITY,
    combinationType = DEFAULT_COMBINATIONTYPE,
    simplifiedConfiguration = false,
    values,
    isWorkbook = false
  }: LikeConditionOptions = {}) {
    super();
    this._conditionApiSupplier = conditionApiSupplier;
    this._filterType = filterType;
    this._caseSensitivity = caseSensitivity;
    this._combinationType = combinationType;
    this._simplifiedConfiguration = simplifiedConfiguration;
    this._value = values?.valueItems as DefaultConditionValueItemDto[] || [];
  }

  override connectedCallback () {
    this.classList.add(name);
    this.#dropdownButton = createIconButton(
      icon(searchIcon),
      i18n('dialog-trigger.title'),
      { dropdown: true, variant: 'borderless', styleClass: 'd-like-condition--filter-type-button' });

    this.conditionApi = this._conditionApiSupplier({
      conditionSetId: this.conditionSetId,
      conditionName: this.conditionName,
      conditionId: this.conditionId as ConditionId,
      conditionValuesSetHashGetter: () => this.conditionValuesSetHash
    });

    let dropdown: LikeConditionDropdown;
    setupPopupButton(
      this.#dropdownButton,
      async () => {
        dropdown = new LikeConditionDropdown({
          filterType: this.filterType,
          caseSensitivity: this.caseSensitivity,
          combinationType: this.combinationType,
          simplifiedConfiguration: this._simplifiedConfiguration,
          dataSource: undefined
        });
        return dropdown;
      },
      {
        onClose: () => {
          this.filterType = dropdown.returnValue.filterType as FilterType;
          this.caseSensitivity = dropdown.returnValue.caseSensitivity as CaseSensitivity;
          this.combinationType = dropdown.returnValue.combinationType as CombinationType;
          // Previously we only triggered `onConditionChange` when we also had values set.
          // This caused issues when no value was set, as settings wouldn't be persisted and get lost on reload.
          // Always sending the settings will cause some additional (perhaps unnecessary invalidation),
          // but prevents inconsistencies to crop up e. g. when extracting the settings with a report.
          // A bigger fix would be to introduce seperate condition APIs for *only* updating settings
          // (assuming that e. g. in the workbook context `SET_CONDITION_SETTING` won't invalidate,
          // but only `SET_CONDITION` does).
          this.onConditionChange();
        }
      });

    this._selectInput = new Select({
      tags: true,
      placeholder: i18n('common:select2.placeholder'),
      maxSelection: this.maxSelection, // from parent class
      multiple: true
    });

    this._selectInput.disabled = !this.enabled;
    this.#dropdownButton.disabled = this._selectInput.disabled;
    this._selectInput.addEventListener('change', () => {
      this.onConditionChange();
    });

    const contentBox = h('.table-condition-box');
    contentBox.append(this.#dropdownButton, this._selectInput);
    this.append(contentBox);
    // @ts-ignore – In Cadenza Web Classic the condition is preceded by a d-data element, which we are trying to use here ...
    const data = (this.previousElementSibling as Data).value;
    if (data) {
      this._filterType = data.value.filterType;
      this._caseSensitivity = data.value.caseSensitivity;
      this._combinationType = data.value.combinationType;
      this.value = data.value.rows.flatMap((row: DefaultConditionValueItemDto) => row.values);
      this.defaultValue = data.defaultValue.rows.flatMap((row: DefaultConditionValueItemDto) => row.values);
    } else { // ... while in Cadenza Workbooks, it's some random other element that (hopefully) does not have a `value` property.
      this.value = this.prepareRows(this._value as DefaultConditionValueItemDto[]);
      this.defaultValue = [];
    }
    this.initChanged();
  }

  prepareRows (rows: DefaultConditionValueItemDto[]) {
    return rows.map((row: DefaultConditionValueItemDto & { value?: string[]; id?: string }) => {
      row.value = row.values;
      row.id = row.values.join();
      row.text = row.id;
      return row;
    });
  }

  override initChanged () {
    this.changed = !isEqual(this.value, this.defaultValue);
  }

  override get value () {
    return this._selectInput.selection.map(value => value.id);
  }

  override set value (value: string[] | DefaultConditionValueItemDto[]) {
    this._selectInput.selection = value;
  }

  get filterType () {
    return this._filterType;
  }

  set filterType (filterType) {
    this._filterType = filterType;
  }

  get caseSensitivity () {
    return this._caseSensitivity;
  }

  set caseSensitivity (caseSensitivity) {
    this._caseSensitivity = caseSensitivity;
  }

  get combinationType () {
    return this._combinationType;
  }

  set combinationType (combinationType) {
    this._combinationType = combinationType;
  }

  override onConditionChange () {
    // the filter controller is not available in workbooks
    this.filterController?.disableOtherConditionsIfNeededForConsistency([ this ]);
    this.querySelectorAll('.select2-search-choice').forEach(choice => { choice.setAttribute('title', i18n(`filtertype.${this._filterType.toLowerCase()}`)); });
    const setValuesPromise = this.conditionApi.setValue(this.filterType, this.caseSensitivity, this.combinationType, this.value);
    this.#latestSetValuesPromise = setValuesPromise;
    setValuesPromise
      .then(json => super.onConditionChange(json))
      .catch(error => this.handleConditionValueChangeError(error))
      .finally(() => {
        // if the changes are not fully processed yet, disable the other conditions again.
        const enable = setValuesPromise === this.#latestSetValuesPromise;
        // the filter controller is not availabe in workbooks
        this.filterController?.setConditionsDisabledForConsistency(enable, this);
      });
  }

  override get hasValue () {
    return this.value.length > 0;
  }

}

customElements.define(name, LikeCondition);
